import { ButtonPastille, Icon } from '@amelio/ui-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { openSkillsetAssessmentPdf } from './skillsetAssessmentPdf';
import { SkillsetAssessmentPdfDocument } from './SkillsetAssessmentPdfDocument';

import type { SkillsetAssessmentPdfDocumentProps } from './SkillsetAssessmentPdfDocument';
import type { SkillCategory } from '@/types/performance-profile';

interface SkillsetAssessmentPrintButtonProps {
    employeeName: string;
    competencyProfileName: string | null;
    categories: SkillCategory[];
    /** The same legend the card shows on screen, so the document says what the reader saw. */
    legend: SkillsetAssessmentPdfDocumentProps['legend'];
}

/**
 * Exports the competency assessment as a PDF built on the client. An employee with no competency
 * assessed yet is an ordinary, expected state, not a fault: the button is unavailable, and the
 * card's own empty state already tells the reader why.
 *
 * While the export runs, the printable document is rendered here, off-screen, as part of this
 * tree, and handed to the capture as an element. A root of its own never committed it once the
 * module was embedded in Engagement (#16580): see {@link openSkillsetAssessmentPdf}.
 */
export const SkillsetAssessmentPrintButton = ({
    employeeName,
    competencyProfileName,
    categories,
    legend,
}: SkillsetAssessmentPrintButtonProps) => {
    const { t } = useTranslation();
    const [isExporting, setIsExporting] = useState(false);
    const documentRef = useRef<HTMLDivElement>(null);
    const generatedAt = useMemo(() => new Date(), []);

    const hasNothingToPrint =
        categories.reduce((sum, category) => sum + category.skills.length, 0) === 0;

    // The document exists only while exporting, so it is captured once it has been committed:
    // after the render that shows it, never from the click that asked for it.
    useEffect(() => {
        const element = documentRef.current;
        if (!isExporting || !element) {
            return;
        }
        openSkillsetAssessmentPdf(element)
            .catch((error: unknown) => {
                console.error('Skillset assessment PDF export failed', error);
            })
            .finally(() => {
                setIsExporting(false);
            });
    }, [isExporting]);

    return (
        <>
            <ButtonPastille
                onClick={() => setIsExporting(true)}
                loading={isExporting}
                disabled={isExporting || hasNothingToPrint}
                leftIcon={
                    <Icon
                        name={'electronics-printer'}
                        size={18}
                        color={'inherit'}
                        ariaHidden
                    />
                }
                dataTestId={'skillset-assessment-print'}
            >
                {t('performanceProfile.skillsetAssessment.print')}
            </ButtonPastille>
            {isExporting && (
                // Off-screen rather than hidden: the capture needs a laid-out document.
                <div
                    className={'pointer-events-none fixed top-0 left-[-9999px]'}
                    aria-hidden
                    data-testid={'skillset-assessment-print-document'}
                >
                    <SkillsetAssessmentPdfDocument
                        ref={documentRef}
                        employeeName={employeeName}
                        competencyProfileName={competencyProfileName}
                        generatedAt={generatedAt}
                        categories={categories}
                        legend={legend}
                    />
                </div>
            )}
        </>
    );
};
